import React, { useState, useEffect } from 'react';
import { Database, RefreshCw, AlertCircle, Loader2, Eye, Shield, AlertTriangle, Info, ArrowLeft, Calendar, Car, FileText, User, Phone, MapPin, AlertCircle as AlertIcon, CheckCircle, Clock } from 'lucide-react';
import { getRecords, analyzeFraud } from '../api/client';
import { getFieldLabel } from '../constants';
import { useNavigate, useParams } from 'react-router-dom';

// Main Records List Page
export default function RecordsPage() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    
    // Fetch all records
    const fetchRecords = async () => {
        setLoading(true);
        setError("");
        try {
            const recordsData = await getRecords();
            setRecords(recordsData || []);
        } catch (err) {
            console.error("Fetch records error:", err);
            setError("Erreur réseau lors du chargement des dossiers");
        } finally {
            setLoading(false);
        }
    };

    // Load records on component mount
    useEffect(() => {
        fetchRecords();
    }, []);

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString("fr-FR", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return dateString;
        }
    };

    // Get risk color function
    const getRiskColor = (riskLevel) => {
        switch (riskLevel?.toLowerCase()) {
            case "élevé":
                return "text-red-600 bg-red-100";
            case "moyen":
                return "text-orange-600 bg-orange-100";
            case "faible":
                return "text-green-600 bg-green-100";
            default:
                return "text-gray-600 bg-gray-100";
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <Database className="w-8 h-8 text-blue-600" />
                        Liste des Dossiers Expertise
                    </h1>
                    <button
                        onClick={fetchRecords}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        {loading ? "Chargement..." : "Actualiser"}
                    </button>
                </div>

                {/* Error message */}
                {error && (
                    <div className="p-4 mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg font-medium flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Records table */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            <span className="ml-2 text-gray-600">Chargement des dossiers...</span>
                        </div>
                    ) : records.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg">Aucun dossier trouvé</p>
                            <p className="text-sm">Les dossiers extraits apparaîtront ici</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Fichier
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date de création
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Véhicule
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Immatriculation
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {records.map((record) => (
                                        <tr key={record.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {record.original_filename}
                                                </div>
                                                <div className="text-sm text-gray-500">{record.id}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(record.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {record.marque || "N/A"}
                                                </div>
                                                <div className="text-sm text-gray-500">{record.type_vehicule || ""}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.immatriculation || "N/A"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <a
                                                    href={`/records/${record.id}`}
                                                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-xs font-medium"
                                                >
                                                    <Eye className="w-3 h-3" />
                                                    Voir Détails
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}